import { useId } from 'react';
import { crestForSeed, type Charge, type CrestSpec, type Division } from '@pk/shared';

const SHIELD = 'M6,6 H94 V58 C94,92 70,110 50,118 C30,110 6,92 6,58 Z';

function star(cx: number, cy: number, r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.42;
    pts.push(`${(cx + Math.cos(a) * rr).toFixed(2)},${(cy + Math.sin(a) * rr).toFixed(2)}`);
  }
  return `M${pts.join('L')}Z`;
}

/** Coat of arms from a house heraldry seed (designed crest if in the reserved range, else procedural). */
export function Heraldry({ seed, size = 40, className }: { seed: number; size?: number; className?: string }) {
  return <Shield spec={crestForSeed(seed)} size={size} className={className} />;
}

/** Coat of arms from an explicit crest spec. */
export function Shield({ spec, size = 40, className }: { spec: CrestSpec; size?: number; className?: string }) {
  const uid = useId().replace(/:/g, '');
  const division: Division = spec.division;
  const field = spec.field;
  const second = spec.second;
  const chargeCol = spec.chargeColor;
  const charge: Charge = spec.charge;

  const divShape = () => {
    switch (division) {
      case 'pale': return <rect x="50" y="0" width="60" height="130" fill={second} />;
      case 'fess': return <rect x="0" y="62" width="100" height="70" fill={second} />;
      case 'bend': return <path d="M0,0 L100,0 L100,10 L10,130 L0,130Z" fill={second} transform="translate(0,0)" opacity={1} />;
      case 'quarterly': return <g fill={second}><rect x="50" y="0" width="50" height="62" /><rect x="0" y="62" width="50" height="70" /></g>;
      case 'chevron': return <path d="M-4,92 L50,40 L104,92 L104,112 L50,60 L-4,112Z" fill={second} />;
      case 'saltire': return <path d="M0,10 L10,0 L100,100 L90,110Z M100,10 L90,0 L0,100 L10,110Z" fill={second} />;
      case 'chief': return <rect x="0" y="0" width="100" height="34" fill={second} />;
      default: return null;
    }
  };

  const chargeShape = () => {
    const cx = 50;
    const cy = division === 'chief' ? 76 : division === 'chevron' ? 52 : 62;
    const c = chargeCol;
    switch (charge) {
      case 'roundel': return <circle cx={cx} cy={cy} r={13} fill={c} stroke="#000" strokeOpacity={0.35} />;
      case 'star': return <path d={star(cx, cy, 17)} fill={c} stroke="#000" strokeOpacity={0.35} />;
      case 'cross': return <path d={`M${cx - 5},${cy - 17} h10 v12 h12 v10 h-12 v14 h-10 v-14 h-12 v-10 h12Z`} fill={c} stroke="#000" strokeOpacity={0.35} />;
      case 'crescent': return <path d={`M${cx + 8},${cy - 15} A16,16 0 1 0 ${cx + 8},${cy + 15} A12,12 0 1 1 ${cx + 8},${cy - 15}Z`} fill={c} stroke="#000" strokeOpacity={0.35} />;
      case 'lozenge': return <path d={`M${cx},${cy - 18} L${cx + 12},${cy} L${cx},${cy + 18} L${cx - 12},${cy}Z`} fill={c} stroke="#000" strokeOpacity={0.35} />;
      case 'tower': return <path d={`M${cx - 11},${cy + 15} V${cy - 6} h4 v-5 h5 v5 h4 v-5 h5 v5 h4 v-5 h5 v5 h-4 V${cy + 15}Z`} fill={c} stroke="#000" strokeOpacity={0.35} transform={`translate(0,0)`} />;
      case 'trefoil': return <g fill={c} stroke="#000" strokeOpacity={0.3}><circle cx={cx} cy={cy - 8} r={8} /><circle cx={cx - 9} cy={cy + 6} r={8} /><circle cx={cx + 9} cy={cy + 6} r={8} /></g>;
    }
  };

  return (
    <svg viewBox="0 0 100 124" width={size} height={size * 1.24} className={className} role="img" aria-hidden="true">
      <defs><clipPath id={`sh${uid}`}><path d={SHIELD} /></clipPath></defs>
      <g clipPath={`url(#sh${uid})`}>
        <rect width="100" height="124" fill={field} />
        {divShape()}
        {chargeShape()}
      </g>
      <path d={SHIELD} fill="none" stroke="#0b0907" strokeWidth={3} />
      <path d={SHIELD} fill="none" stroke="#a88a45" strokeWidth={1} opacity={0.8} />
    </svg>
  );
}
